import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Progress,
  Input,
  InputGroup,
  InputRightAddon,
  useToast,
  Divider,
  Badge,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useClipboard
} from '@chakra-ui/react';
import { 
  FaUsers, 
  FaLink, 
  FaCoins, 
  FaChartLine, 
  FaExchangeAlt, 
  FaCopy, 
  FaTwitter, 
  FaTelegram, 
  FaFacebook,
  FaUserPlus,
  FaMoneyBillWave
} from 'react-icons/fa';
import affiliateService from '../services/affiliateService';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const AffiliateDashboard = () => {
  const toast = useToast();
  const [stats, setStats] = useState({
    totalReferrals: 0,
    activeReferrals: 0,
    totalEarnings: 0,
    pendingEarnings: 0,
    claimedEarnings: 0,
    conversionRate: 0,
    referralLink: '',
    tier: 'Bronze',
    nextTierProgress: 0,
    nextTierRequirement: 10,
    stakedAmount: 0
  });
  
  const [chartData, setChartData] = useState({
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
    datasets: [
      {
        label: 'Referrals',
        data: [3, 5, 8, 12, 15, 20, 25],
        borderColor: '#B91C1C',
        backgroundColor: 'rgba(185, 28, 28, 0.1)',
        tension: 0.4
      },
      {
        label: 'Earnings (JINU)',
        data: [50, 120, 200, 350, 500, 750, 1000],
        borderColor: '#38A169',
        backgroundColor: 'rgba(56, 161, 105, 0.1)',
        tension: 0.4
      }
    ]
  });
  
  const [referralHistory, setReferralHistory] = useState([
    { address: '0x123...abc', date: '2025-04-10', status: 'Active', earnings: 150 },
    { address: '0x456...def', date: '2025-04-08', status: 'Active', earnings: 120 },
    { address: '0x789...ghi', date: '2025-04-05', status: 'Inactive', earnings: 80 },
    { address: '0xabc...123', date: '2025-04-01', status: 'Active', earnings: 200 },
    { address: '0xdef...456', date: '2025-03-28', status: 'Active', earnings: 175 }
  ]);
  
  const { hasCopied, onCopy } = useClipboard(stats.referralLink);
  
  // Fetch affiliate stats
  useEffect(() => {
    const fetchAffiliateStats = async () => {
      try {
        // In a real implementation, this would call the affiliateService
        // For now, we'll use mock data
        
        // Generate referral link
        const referralLink = window.location.origin + '?ref=YOUR_WALLET_ADDRESS';
        
        setStats({
          totalReferrals: 25,
          activeReferrals: 18,
          totalEarnings: 1500,
          pendingEarnings: 350,
          claimedEarnings: 1150,
          conversionRate: 72,
          referralLink: referralLink,
          tier: 'Silver',
          nextTierProgress: 65,
          nextTierRequirement: 50,
          stakedAmount: 5000
        });
      } catch (error) {
        console.error('Failed to fetch affiliate stats:', error);
        toast({
          title: 'Error',
          description: 'Failed to load affiliate data. Please try again later.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };
    
    fetchAffiliateStats();
  }, [toast]);
  
  const handleClaimRewards = () => {
    toast({
      title: 'Rewards Claimed',
      description: `You've successfully claimed ${stats.pendingEarnings} JINU!`,
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
    
    // Update stats
    setStats({
      ...stats,
      pendingEarnings: 0,
      claimedEarnings: stats.claimedEarnings + stats.pendingEarnings
    });
  };
  
  const handleStake = (amount) => {
    toast({
      title: 'Staking Successful',
      description: `You've staked ${amount} JINU to boost your affiliate rewards!`,
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
    
    // Update stats
    setStats({
      ...stats,
      stakedAmount: stats.stakedAmount + amount
    });
  };
  
  const handleShareOnSocial = (platform) => {
    // In a real implementation, this would open a share dialog
    toast({
      title: `Share on ${platform}`,
      description: 'Sharing functionality would be implemented here.',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };
  
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Affiliate Performance',
      },
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };
  
  return (
    <Box>
      <Heading size="lg" mb={6} color="#B91C1C">Affiliate Dashboard</Heading>
      
      {/* Stats Overview */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
        <Stat bg="rgba(45, 55, 72, 0.7)" p={4} borderRadius="lg" boxShadow="md">
          <StatLabel color="gray.300">Total Referrals</StatLabel>
          <Flex align="center">
            <Icon as={FaUsers} color="#B91C1C" mr={2} />
            <StatNumber color="white">{stats.totalReferrals}</StatNumber>
          </Flex>
          <StatHelpText color="gray.400">
            <StatArrow type="increase" />
            {stats.activeReferrals} active
          </StatHelpText>
        </Stat>
        
        <Stat bg="rgba(45, 55, 72, 0.7)" p={4} borderRadius="lg" boxShadow="md">
          <StatLabel color="gray.300">Total Earnings</StatLabel>
          <Flex align="center">
            <Icon as={FaCoins} color="#B91C1C" mr={2} />
            <StatNumber color="white">{stats.totalEarnings} JINU</StatNumber>
          </Flex>
          <StatHelpText color="gray.400">
            Conversion rate: {stats.conversionRate}%
          </StatHelpText>
        </Stat>
        
        <Stat bg="rgba(45, 55, 72, 0.7)" p={4} borderRadius="lg" boxShadow="md">
          <StatLabel color="gray.300">Pending Rewards</StatLabel>
          <Flex align="center">
            <Icon as={FaMoneyBillWave} color="#B91C1C" mr={2} />
            <StatNumber color="white">{stats.pendingEarnings} JINU</StatNumber>
          </Flex>
          <Button 
            size="sm" 
            colorScheme="red" 
            mt={2} 
            isDisabled={stats.pendingEarnings === 0}
            onClick={handleClaimRewards}
          >
            Claim Rewards
          </Button>
        </Stat>
        
        <Stat bg="rgba(45, 55, 72, 0.7)" p={4} borderRadius="lg" boxShadow="md">
          <StatLabel color="gray.300">Affiliate Tier</StatLabel>
          <Flex align="center">
            <Badge colorScheme="red" fontSize="lg" p={1}>
              {stats.tier}
            </Badge>
          </Flex>
          <Box mt={2}>
            <Text color="gray.400" fontSize="sm">Next Tier Progress</Text>
            <Progress value={stats.nextTierProgress} colorScheme="red" size="sm" borderRadius="full" mt={1} />
            <Text color="gray.400" fontSize="xs" mt={1}>
              {stats.nextTierProgress}% of {stats.nextTierRequirement} referrals
            </Text>
          </Box>
        </Stat>
      </SimpleGrid>
      
      {/* Referral Link */}
      <Box bg="rgba(45, 55, 72, 0.7)" p={6} borderRadius="lg" mb={8} boxShadow="md">
        <Heading size="md" mb={4} color="#B91C1C">Your Referral Link</Heading>
        <InputGroup size="md">
          <Input 
            value={stats.referralLink} 
            isReadOnly 
            bg="gray.800" 
            color="white"
            borderColor="gray.600"
          />
          <InputRightAddon 
            children={<Icon as={FaCopy} />} 
            bg="#B91C1C" 
            color="white" 
            cursor="pointer"
            onClick={onCopy}
          />
        </InputGroup>
        {hasCopied && (
          <Text color="green.400" fontSize="sm" mt={2}>
            Copied to clipboard!
          </Text>
        )}
        
        <HStack spacing={4} mt={4}>
          <Button 
            leftIcon={<FaTwitter />} 
            colorScheme="red" 
            size="sm"
            onClick={() => handleShareOnSocial('Twitter')}
          >
            Share
          </Button>
          <Button 
            leftIcon={<FaTelegram />} 
            colorScheme="red" 
            size="sm"
            onClick={() => handleShareOnSocial('Telegram')}
          >
            Share
          </Button>
          <Button 
            leftIcon={<FaFacebook />} 
            colorScheme="red" 
            size="sm"
            onClick={() => handleShareOnSocial('Facebook')}
          >
            Share
          </Button>
        </HStack>
      </Box>
      
      {/* Tabs for Analytics and Settings */}
      <Tabs colorScheme="red" bg="rgba(45, 55, 72, 0.7)" borderRadius="lg" boxShadow="md">
        <TabList borderBottomColor="gray.700">
          <Tab _selected={{ color: 'white', borderColor: '#B91C1C' }}>Analytics</Tab>
          <Tab _selected={{ color: 'white', borderColor: '#B91C1C' }}>Referrals</Tab>
          <Tab _selected={{ color: 'white', borderColor: '#B91C1C' }}>Staking</Tab>
        </TabList>
        
        <TabPanels>
          {/* Analytics Panel */}
          <TabPanel>
            <Box h="300px">
              <Line options={chartOptions} data={chartData} />
            </Box>
            
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mt={6}>
              <Stat bg="whiteAlpha.100" p={4} borderRadius="md">
                <StatLabel color="gray.300">Daily Average</StatLabel>
                <StatNumber color="#B91C1C">3.5</StatNumber>
                <StatHelpText color="gray.400">
                  <StatArrow type="increase" />
                  23.36%
                </StatHelpText>
              </Stat>
              
              <Stat bg="whiteAlpha.100" p={4} borderRadius="md">
                <StatLabel color="gray.300">Monthly Growth</StatLabel>
                <StatNumber color="#B91C1C">25%</StatNumber>
                <StatHelpText color="gray.400">
                  <StatArrow type="increase" />
                  5.14%
                </StatHelpText>
              </Stat>
              
              <Stat bg="whiteAlpha.100" p={4} borderRadius="md">
                <StatLabel color="gray.300">Avg. Earnings/Referral</StatLabel>
                <StatNumber color="#B91C1C">60 JINU</StatNumber>
                <StatHelpText color="gray.400">
                  <StatArrow type="increase" />
                  10.23%
                </StatHelpText>
              </Stat>
            </SimpleGrid>
          </TabPanel>
          
          {/* Referrals Panel */}
          <TabPanel>
            <Box overflowX="auto">
              <Table variant="simple" colorScheme="whiteAlpha">
                <Thead>
                  <Tr>
                    <Th color="gray.400">Address</Th>
                    <Th color="gray.400">Date</Th>
                    <Th color="gray.400">Status</Th>
                    <Th color="gray.400" isNumeric>Earnings (JINU)</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {referralHistory.map((referral, index) => (
                    <Tr key={index}>
                      <Td color="white">{referral.address}</Td>
                      <Td color="white">{referral.date}</Td>
                      <Td>
                        <Badge colorScheme={referral.status === 'Active' ? 'green' : 'gray'}>
                          {referral.status}
                        </Badge>
                      </Td>
                      <Td color="#B91C1C" isNumeric>{referral.earnings}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
            
            <Button colorScheme="red" size="sm" mt={4} leftIcon={<FaUserPlus />}>
              Invite More Friends
            </Button>
          </TabPanel>
          
          {/* Staking Panel */}
          <TabPanel>
            <Box bg="whiteAlpha.100" p={4} borderRadius="md" mb={6}>
              <Heading size="sm" mb={3} color="#B91C1C">Staking Boost</Heading>
              <Text color="gray.300" mb={4}>
                Stake JINU tokens to boost your affiliate rewards. The more you stake, the higher your commission rate.
              </Text>
              
              <Flex justify="space-between" align="center" mb={4}>
                <Text color="white">Currently Staked:</Text>
                <Text color="#B91C1C" fontWeight="bold">{stats.stakedAmount} JINU</Text>
              </Flex>
              
              <Flex justify="space-between" align="center" mb={4}>
                <Text color="white">Current Boost:</Text>
                <Badge colorScheme="red">+{Math.min(50, Math.floor(stats.stakedAmount / 100))}%</Badge>
              </Flex>
              
              <Divider my={4} borderColor="gray.700" />
              
              <Heading size="sm" mb={3} color="#B91C1C">Stake More</Heading>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Button colorScheme="red" variant="outline" onClick={() => handleStake(1000)}>
                  Stake 1,000 JINU
                </Button>
                <Button colorScheme="red" variant="outline" onClick={() => handleStake(5000)}>
                  Stake 5,000 JINU
                </Button>
                <Button colorScheme="red" variant="outline" onClick={() => handleStake(10000)}>
                  Stake 10,000 JINU
                </Button>
              </SimpleGrid>
            </Box>
            
            <Box bg="whiteAlpha.100" p={4} borderRadius="md">
              <Heading size="sm" mb={3} color="#B91C1C">Staking Tiers</Heading>
              <Table variant="simple" colorScheme="whiteAlpha" size="sm">
                <Thead>
                  <Tr>
                    <Th color="gray.400">Tier</Th>
                    <Th color="gray.400">Staking Requirement</Th>
                    <Th color="gray.400">Boost</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td color="white">Bronze</Td>
                    <Td color="white">0 JINU</Td>
                    <Td color="#B91C1C">+0%</Td>
                  </Tr>
                  <Tr>
                    <Td color="white">Silver</Td>
                    <Td color="white">1,000 JINU</Td>
                    <Td color="#B91C1C">+10%</Td>
                  </Tr>
                  <Tr>
                    <Td color="white">Gold</Td>
                    <Td color="white">5,000 JINU</Td>
                    <Td color="#B91C1C">+25%</Td>
                  </Tr>
                  <Tr>
                    <Td color="white">Platinum</Td>
                    <Td color="white">10,000 JINU</Td>
                    <Td color="#B91C1C">+50%</Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default AffiliateDashboard;
